import { IntegrationPlan } from '@/models/traceability';
import i18n from '@/plugins/i18n';
import { TraceabilityPackageStatus } from '@/services/api/models/traceability';

export const TESTING_STATE_OPTIONS = {
  AwaitingConfirmation: {
    displayName: 'Awaiting Confirmation',
    iconColor: 'ice-700',
    iconName: 'ellipsis',
    id: 'AwaitingConfirmation',
  },
  NotRequired: {
    displayName: 'Not Required',
    iconColor: 'ice-700',
    iconName: 'circle-partial',
    id: 'NotRequired',
  },
  NotSubmitted: {
    displayName: 'Not Submitted',
    iconColor: 'ice-700',
    iconName: 'history',
    id: 'NotSubmitted',
  },
  ProcessValidated: {
    displayName: 'Process Validated',
    iconColor: 'ice-700',
    iconName: 'clipboard-checkmark',
    id: 'ProcessValidated',
  },
  Remediated: {
    displayName: 'Remediated',
    iconColor: 'ice-700',
    iconName: 'swap-horizontal',
    id: 'Remediated',
  },
  RetestFailed: {
    displayName: 'Retest Failed',
    iconColor: 'red-500',
    iconName: 'close',
    id: 'RetestFailed',
  },
  RetestPassed: {
    displayName: 'Retest Passed',
    iconColor: 'teal-500',
    iconName: 'check',
    id: 'RetestPassed',
  },
  SelectedForRandomTesting: {
    displayName: 'Selected For Random Testing',
    iconColor: 'ice-700',
    iconName: 'logout',
    id: 'SelectedForRandomTesting',
  },
  SubmittedForTesting: {
    displayName: 'Submitted For Testing',
    iconColor: 'ice-700',
    iconName: 'submit',
    id: 'SubmittedForTesting',
  },
  TestFailed: {
    displayName: 'Test Failed',
    iconColor: 'red-500',
    iconName: 'close',
    id: 'TestFailed',
  },
  TestPassed: {
    displayName: 'Test Passed',
    iconColor: 'teal-500',
    iconName: 'check',
    id: 'TestPassed',
  },
  TestingInProgress: {
    displayName: 'Testing In Progress',
    iconColor: 'teal-500',
    iconName: 'ellipsis',
    id: 'TestingInProgress',
  },
};

export const PACKAGE_STATUS_OPTIONS = {
  ACTIVE: {
    id: TraceabilityPackageStatus.Active,
    name: TraceabilityPackageStatus.Active,
  },
  INACTIVE: {
    id: TraceabilityPackageStatus.Inactive,
    name: TraceabilityPackageStatus.Inactive,
  },
  ONHOLD: {
    id: TraceabilityPackageStatus.OnHold,
    name: TraceabilityPackageStatus.OnHold,
  },
};

export const MEASUREMENT_UNITS = {
  PERCENTAGE: {
    value: 'PERCENTAGE',
    label: '%',
  },
  MG_PER_ML: {
    value: 'MG_PER_ML',
    label: 'MG/ML',
  },
  MG_PER_G: {
    value: 'MG_PER_G',
    label: 'MG/G',
  },
  MG_PER_UNIT: {
    value: 'MG_PER_UNIT',
    label: 'MG/Unit',
  },
};

/**
 * @deprecated use LabTestingState enum or LabTestingStates type from models/traceability.ts
 */
export const testingStateOptions = [
  'AwaitingConfirmation',
  'NotRequired',
  'NotSubmitted',
  'ProcessValidated',
  'Remediated',
  'RetestFailed',
  'RetestPassed',
  'SelectedForRandomTesting',
  'SubmittedForTesting',
  'TestFailed',
  'TestPassed',
  'TestingInProgress',
];

// @deprecated use enum SeedToSaleVendor or type SeedToSaleVendors over services/api/models/traceability.ts
export const SEED_TO_SALE_VENDORS = Object.freeze({
  BIOTRACK: 'BioTrack',
  METRC: 'METRC',
} as const);

export const INTEGRATIONS_BY_STATE = {
  AK: SEED_TO_SALE_VENDORS.METRC,
  AR: SEED_TO_SALE_VENDORS.BIOTRACK,
  CA: SEED_TO_SALE_VENDORS.METRC,
  CO: SEED_TO_SALE_VENDORS.METRC,
  FL: SEED_TO_SALE_VENDORS.BIOTRACK,
  HI: SEED_TO_SALE_VENDORS.BIOTRACK,
  IL: SEED_TO_SALE_VENDORS.BIOTRACK,
  MA: SEED_TO_SALE_VENDORS.METRC,
  MD: SEED_TO_SALE_VENDORS.METRC,
  ME: SEED_TO_SALE_VENDORS.METRC,
  MI: SEED_TO_SALE_VENDORS.METRC,
  MO: SEED_TO_SALE_VENDORS.METRC,
  MS: SEED_TO_SALE_VENDORS.METRC,
  ND: SEED_TO_SALE_VENDORS.BIOTRACK,
  NH: SEED_TO_SALE_VENDORS.BIOTRACK,
  NM: SEED_TO_SALE_VENDORS.BIOTRACK,
  NV: SEED_TO_SALE_VENDORS.METRC,
  NY: SEED_TO_SALE_VENDORS.BIOTRACK,
  OH: SEED_TO_SALE_VENDORS.METRC,
  OK: SEED_TO_SALE_VENDORS.METRC,
  OR: SEED_TO_SALE_VENDORS.METRC,
  PR: SEED_TO_SALE_VENDORS.BIOTRACK,
} as const;

export const SEED_TO_SALE_COMPONENT_NAMES = {
  FORM_VIEW: 'formView',
  READ_ONLY_VIEW: 'readOnlyView',
} as const;

export const PACKAGE_ID_DATA = Object.freeze({
  [SEED_TO_SALE_VENDORS.BIOTRACK]: {
    placeholder: 'seedToSale.packages.filters.sourcePackageId.placeholderBioTrack',
    noOptions: 'seedToSale.packages.filters.sourcePackageId.digitCountRuleBioTrack',
    searchLength: [16, 18],
  },
  [SEED_TO_SALE_VENDORS.METRC]: {
    placeholder: 'seedToSale.packages.filters.sourcePackageId.placeholderMetrc',
    noOptions: 'seedToSale.packages.filters.sourcePackageId.digitCountRuleMetrc',
    searchLength: [24],
  },
});

export const getIntegrationPlans = (): IntegrationPlan[] => [
  {
    id: 'verified',
    color: 'orange',
    description: i18n.t('settings.integrationPlans.verified.description'),
    name: i18n.t('settings.integrationPlans.verified.name'),
    features: [
      {
        icon: 'license-certificate',
        label: i18n.t('settings.integrationPlans.features.licenseVerification'),
      },
      {
        icon: 'badge-seller-verified',
        label: i18n.t('settings.integrationPlans.features.verifiedBadge'),
      },
      {
        icon: 'heart-outline',
        label: i18n.t('settings.integrationPlans.features.customerTrust'),
      },
    ],
    recommended: false,
    verificationMethod: i18n.t('settings.integrationPlans.verified.verificationMethod'),
  },
  {
    id: 'compliant',
    color: 'yellow',
    description: i18n.t('settings.integrationPlans.compliant.description'),
    name: i18n.t('settings.integrationPlans.compliant.name'),
    features: [
      {
        icon: 'tag',
        label: i18n.t('settings.integrationPlans.features.complianceTags'),
      },
      {
        icon: 'license-approved',
        label: i18n.t('settings.integrationPlans.features.buyerCOAs'),
      },
      {
        icon: 'document-invoice',
        label: i18n.t('settings.integrationPlans.features.lastUpdatedInventory'),
      },
      {
        icon: 'truck',
        label: i18n.t('settings.integrationPlans.features.compliantShippingManifests'),
      },
      {
        icon: 'graph-bar-chart',
        label: i18n.t('settings.integrationPlans.features.inventoryDemandInsights'),
      },
    ],
    recommended: true,
    verificationMethod: i18n.t('settings.integrationPlans.compliant.verificationMethod'),
  },
];
